/**
 * The sidebar: one short word per thing, and each thing only once.
 *
 * This list has been wrong twice, in opposite directions. First it was the
 * module tree — two hundred entries named after the part of the system they
 * belonged to, which nobody could navigate because nobody knows which module
 * a receipt was built in. Then it was a list of sentences — "Take money from
 * a shop", "Move stock between godowns", "How the business is doing" — which
 * reads beautifully once and is unusable every day after, because a sidebar
 * is not read, it is scanned. Twelve of the twenty-three rows were longer
 * than fourteen characters, several were the same place reached twice, and
 * the owner's verdict was the right one: a headache.
 *
 * So: one or two words, the noun of the thing wherever a noun will do, and
 * nothing that appears somewhere else on the same screen. Shops was a task
 * AND a browse link AND "Add a shop"; it is now Shops, and you add one from
 * there, which is where you were going to end up anyway.
 *
 * The four bands are four questions, and a person usually lives in one:
 *
 *   work      the day's doing — the salesman and the clerk live here
 *   approve   somebody is waiting on me — the manager lives here
 *   staff     the people — the office lives here
 *   mine      my own attendance, pay and leave — everyone has this
 *
 * Each row names the permission it needs, so a salesman sees six rows and the
 * owner seventeen. Nobody is shown a door that is locked, and nobody is shown
 * the same door twice.
 *
 * What is NOT here is deliberate. Adding a shop, taking an order, seeing
 * which shops have asked for one — those are things you do from inside Shops
 * and Orders, where the shop and its history already are. A menu entry for
 * each would be a second, worse way to do what the screen does better, and
 * that is how the list got to twenty-three rows in the first place.
 */

export interface Task {
  /** Stable id, used for ordering and for remembering what someone uses. */
  id: string;
  /** One or two words. If it needs more, it is probably two things. */
  label: string;
  /** One line under it, shown on the home screen only, never in the sidebar. */
  hint?: string;
  /** Where it goes. */
  to: string;
  /** Lucide icon name. */
  icon: string;
  /** Permission module this needs; omitted means everyone signed in. */
  module?: string;
  /** Which band it belongs in. */
  group: 'work' | 'approve' | 'staff' | 'me';
  /** Search terms someone might use instead of the label — the long names live here now. */
  keywords?: string;
}

export const TASKS: Task[] = [

  // ---- The day's doing -----------------------------------------------------
  {
    id: 'my-day',
    label: 'My day',
    hint: 'Your shops and visits for today',
    to: '/field/my-day',
    icon: 'Footprints',
    module: 'field.visit',
    group: 'work',
    keywords: 'today beat round visit plan route start the day check in field',
  },
  {
    id: 'shops',
    label: 'Shops',
    hint: 'Every shop you sell to',
    to: '/field/outlets',
    icon: 'Store',
    module: 'field.outlet',
    group: 'work',
    keywords: 'retailer outlet customer add a shop new shop dukan',
  },
  {
    id: 'stockists',
    label: 'Stockists',
    hint: 'Who you supply',
    to: '/field/distributors',
    icon: 'Contact',
    module: 'field.distributor',
    group: 'work',
    keywords: 'distributor dms wholesaler party credit',
  },
  {
    id: 'products',
    label: 'Products',
    hint: 'What you sell',
    to: '/masters/products',
    icon: 'Package',
    module: 'master.product',
    group: 'work',
    keywords: 'item goods sku maal rate price add a product',
  },
  {
    id: 'orders',
    label: 'Orders',
    hint: 'Taken, approved and waiting',
    to: '/field/orders',
    icon: 'ShoppingCart',
    module: 'field.order',
    group: 'work',
    keywords: 'order booking take an order shops waiting requests order no',
  },
  /*
   * Schemes and Rates were in the menu and nowhere a person would look.
   *
   * Both were declared, both were correct, and both sat inside "All screens"
   * → "Your lists" → a group heading — three clicks behind a button most
   * people never press, and hidden altogether in Simple view. The owner's
   * report was that they are "not available anywhere", and for anybody not
   * reading the module tree that was true.
   *
   * They belong in the day's doing. A scheme is the reason an order is
   * eighty cases and not sixty, and the rate is the first question a
   * shopkeeper asks.
   */
  {
    id: 'schemes',
    label: 'Schemes',
    hint: 'Offers running, and what they cost',
    to: '/field/schemes',
    icon: 'Gift',
    module: 'erp.scheme',
    group: 'work',
    keywords: 'scheme offer discount free goods foc slab diwali bonanza gift basket payout skim yojana',
  },
  {
    id: 'rates',
    label: 'Rates',
    hint: 'What each product sells for',
    to: '/field/price-lists',
    icon: 'Tags',
    module: 'master.pricelist',
    group: 'work',
    keywords: 'price list rate card ptr mrp pricelist bhav rate chart price sheet',
  },
  {
    id: 'collect',
    label: 'Collect',
    hint: 'Money taken from shops',
    to: '/field/collections',
    icon: 'HandCoins',
    module: 'field.collection',
    group: 'work',
    keywords: 'collection payment receipt cash cheque upi paisa take money',
  },
  {
    id: 'dispatch',
    label: 'Dispatch',
    hint: 'Send goods out, and where they are',
    to: '/erp/dispatch',
    icon: 'Truck',
    module: 'erp.dispatch',
    group: 'work',
    keywords: 'send goods out challan lorry load delivery tracking arrived',
  },
  {
    id: 'stock',
    // This pointed at /reports/stock/summary, which has answered 404 since
    // the accounting routes were removed: the screen loaded, asked the
    // server, got nothing and said "Nothing to show for this view" — which
    // reads as "you have no stock" rather than "this is broken". The real
    // figures were next door at the warehouse the whole time.
    label: 'Stock',
    hint: 'What is in the godowns',
    to: '/erp/warehouse',
    icon: 'Boxes',
    module: 'erp.warehouse.stock',
    group: 'work',
    keywords: 'godown warehouse on hand balance quantity maal batch expiry valuation move stock transfer',
  },

  // ---- Waiting on a person -------------------------------------------------
  {
    id: 'approvals',
    label: 'Approvals',
    hint: 'Late starts, round changes, plans',
    to: '/field/approvals',
    icon: 'CheckCheck',
    module: 'field.visit',
    group: 'approve',
    keywords: 'approve pending waiting manager team yes no late',
  },
  {
    id: 'requests',
    label: 'Requests',
    hint: 'New shops and changes from the field',
    to: '/field/requests',
    icon: 'BellRing',
    module: 'field.request',
    group: 'approve',
    keywords: 'request new outlet edit change field reward issue problem',
  },
  {
    id: 'claims',
    label: 'Claims',
    hint: 'Travel money to approve',
    to: '/field/expenses',
    icon: 'Receipt',
    module: 'field.expense',
    group: 'approve',
    keywords: 'ta da travel expense claim approve settle petrol allowance',
  },

  // ---- The people ----------------------------------------------------------
  {
    id: 'attendance',
    label: 'Attendance',
    hint: 'Who came in today',
    to: '/hr/attendance',
    icon: 'CalendarCheck',
    module: 'hr.attendance',
    group: 'staff',
    keywords: 'present absent register haajri muster punch who came in',
  },
  {
    id: 'staff',
    label: 'Staff',
    hint: 'Everyone on the books',
    to: '/hr/employees',
    icon: 'Users',
    module: 'hr.employee',
    group: 'staff',
    keywords: 'employee people add a person join new hire karmchari',
  },
  {
    id: 'salaries',
    label: 'Salaries',
    hint: 'Run the month and pay out',
    to: '/hr/payroll',
    icon: 'Wallet',
    module: 'hr.payroll',
    group: 'staff',
    keywords: 'payroll salary pagar payslip month run',
  },

  // ---- Mine ----------------------------------------------------------------
  {
    id: 'check-in',
    label: 'Check in',
    hint: 'Mark that you are at work',
    to: '/do/attendance',
    icon: 'Clock',
    group: 'me',
    keywords: 'attendance punch present haajri time mark',
  },
  {
    id: 'my-pay',
    label: 'My pay',
    to: '/hr/me',
    icon: 'CircleUser',
    group: 'me',
    keywords: 'payslip salary my own pagar',
  },
  {
    id: 'my-leave',
    label: 'My leave',
    to: '/hr/leave',
    icon: 'CalendarDays',
    group: 'me',
    keywords: 'leave holiday rajaa absent apply',
  },
];
